import { handleAxiosError } from '@/api/helpers';
import { FiltersType, ListingRequest } from '@/api/types';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import { BranchFormState, DeleteBranchSettings } from '@/client/branches';
import {
  LoadingStateType,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { branchesSchema } from '@/components/branches/forms/validations';
import { FormikSwitch } from '@/components/form';
import { FormikInput } from '@/components/form/FormikInput';
import { FormikLazyDropdown } from '@/components/form/FormikLazyDropdown';
import { useDeleteBranches } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { FlexContainer, FormContainer } from '@/ui/styled-ui';
import { branchAdminCheck, isDemoRestrictedMode } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { Field, Form, Formik, FormikValues } from 'formik';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { DeleteBranchModal } from './modals/DeleteBranchModal';

const StyledParagraph = styled.p`
  font-size: var(--xsmall-font-size);
  margin: 0;
`;

type BranchFormProps = {
  initialValues?: BranchFormState;
  additionalFilters?: FiltersType;
  isCreate?: boolean;
  state?: LoadingStateType;
  onSubmit: (data: FormikValues) => void;
  disabled?: boolean;
};

export const BranchForm: React.FC<BranchFormProps> = ({
  initialValues,
  additionalFilters,
  isCreate,
  state,
  onSubmit,
  disabled,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const isBranchAdmin = branchAdminCheck(user, currentAccount);
  const isDemoAccount = isDemoRestrictedMode(currentAccount, user);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const deleteBranches = useDeleteBranches();
  const handleDeleteBranchWithModal = async (
    settings: DeleteBranchSettings,
  ) => {
    if (isDemoAccount || !id) return;

    try {
      setIsDeleting(true);
      await deleteBranches.delete({
        accountId: (currentAccount as Account)?.id,
        branchId: id,
        shouldDeleteUsers: settings.shouldDeleteUsers,
        reAssignUsersToBranch: settings.reAssignUsersToBranch,
        reAssignChildBranchesTo: settings.reAssignChildBranchesTo,
      });
      toast?.success(t('toast.success'), t('branch.deleted'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setShowDeleteModal(false);
      setIsDeleting(false);
      navigate(RedirectPaths[RedirectPathsEnum.BRANCHES]());
    }
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const fetchBranches = async (params: ListingRequest) =>
    await client.branches.getBranches(
      { ...params, sort: ['name,asc'] },
      currentAccount?.id,
    );

  return (
    <Formik
      enableReinitialize
      initialValues={
        initialValues ?? { name: '', parent: undefined, active: false }
      }
      validationSchema={branchesSchema(
        t,
        isBranchAdmin && user?.branch?.id !== id,
      )}
      onSubmit={onSubmit}
    >
      {({ errors, setFieldValue, setFieldTouched, values }) => (
        <FormContainer>
          <Form className="w-full" autoComplete="off">
            <div className="field w-full mb-4">
              <Field
                id="name"
                name="name"
                label={t('generic.name')}
                className="w-full"
                component={FormikInput}
                placeholder={t('branch.name')}
                required
                disabled={disabled}
              />
            </div>
            {!(user?.branch?.id === id && isBranchAdmin) && (
              <div className="field w-full mb-4">
                <Field
                  id="parent"
                  showClear
                  label={t('branch.parent')}
                  name="parent"
                  filter
                  onChange={(e: DropdownChangeEvent) =>
                    setFieldValue('parent', e.value)
                  }
                  onBlur={() => setFieldTouched('parent', true)}
                  optionLabel="name"
                  className="w-full"
                  component={FormikLazyDropdown}
                  placeholder={t('generic.select')}
                  fetchService={fetchBranches}
                  additionalFilters={additionalFilters}
                  disabled={
                    disabled || (user?.branch?.id === id && isBranchAdmin)
                  }
                  required={isBranchAdmin && user?.branch?.id !== id}
                />
              </div>
            )}
            <div className="field-checkbox">
              <Field
                inputId="active"
                name="active"
                label={t('branch.setActive')}
                component={FormikSwitch}
                disabled={disabled}
              />
            </div>

            {initialValues?.active && !values.active && !isCreate && (
              <>
                <div className="field-checkbox">
                  <Field
                    inputId="keepUsersActive"
                    name="keepUsersActive"
                    label={t('branch.keepUsersActive')}
                    component={FormikSwitch}
                    disabled={disabled}
                  />
                </div>
                {!values.keepUsersActive && (
                  <Message
                    severity="warn"
                    style={{ maxWidth: '550px' }}
                    text={() => (
                      <>
                        <strong className="font-semibold">
                          {t('branch.deactivate.users.warning.heading')}
                        </strong>
                        <StyledParagraph>
                          {values?.usersCount === 1
                            ? t('branch.deactivate.users.warning.info.singular')
                            : t('branch.deactivate.users.warning.info.plural', {
                                count: values?.activeUsersCount,
                              })}
                        </StyledParagraph>
                      </>
                    )}
                    icon="pi pi-info-circle"
                    className="mt-2"
                  />
                )}
              </>
            )}

            <FlexContainer className="mt-4 mb-5" justify="flex-start" gap={6}>
              <AppButton
                isSubmit={!isDemoAccount}
                severity="secondary"
                label={
                  initialValues?.name ? t('button.save') : t('button.create')
                }
                className="w-4"
                state={state}
                isDisabled={!!Object.keys(errors).length || disabled}
              />
              {!isCreate && !(user?.branch?.id === id && isBranchAdmin) && (
                <AppButton
                  type="text"
                  severity="secondary"
                  icon="pi pi-trash"
                  label={t('branch.delete')}
                  onClick={openDeleteModal}
                />
              )}
            </FlexContainer>
          </Form>
          {!isCreate && !(user?.branch?.id === id && isBranchAdmin) && (
            <DeleteBranchModal
              visible={showDeleteModal}
              branchName={initialValues?.name ?? ''}
              branchId={id ?? ''}
              isDeleting={isDeleting}
              onHide={() => {
                setShowDeleteModal(false);
              }}
              onDelete={handleDeleteBranchWithModal}
            />
          )}
        </FormContainer>
      )}
    </Formik>
  );
};
